import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import KingCard from "./KingCard";

gsap.registerPlugin(ScrollTrigger);

/* The King of Spades — fixed on screen, its transform scrubbed by scroll.
   It zig-zags down the page, bouncing off each section's block, and lands
   flat on the table in the final section. */
export default function FallingCard() {
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    let tl;

    const build = () => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      const mobile = W < 768;
      const R = W * (mobile ? 0.22 : 0.26); // horizontal reach to side blocks

      gsap.set(card, {
        x: 0,
        y: H * 0.12,
        rotateZ: 0,
        rotateX: 0,
        rotateY: 0,
        scale: 1,
        "--mb": "0px",
      });

      tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: document.documentElement,
          start: "top top",
          end: "bottom bottom",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      // Hero → release
      tl.to(card, { y: H * 0.2, rotateZ: 25, duration: 0.5, ease: "power1.in" });

      // → Section 2 block (RIGHT): dive to its top-left corner + bounce
      tl.to(card, { x: R, y: H * 0.34, rotateZ: 150, rotateX: 22, rotateY: -20, "--mb": "5px", duration: 0.8, ease: "power2.in" });
      tl.to(card, { y: H * 0.24, rotateZ: 185, "--mb": "0px", duration: 0.28, ease: "power2.out" });

      // → Section 3 block (LEFT): ricochet + bounce
      tl.to(card, { x: -R, y: H * 0.35, rotateZ: -30, rotateX: -18, rotateY: 24, "--mb": "5px", duration: 0.85, ease: "power2.in" });
      tl.to(card, { y: H * 0.25, rotateZ: -65, "--mb": "0px", duration: 0.28, ease: "power2.out" });

      // → Section 4 block (RIGHT): bounce back
      tl.to(card, { x: R, y: H * 0.34, rotateZ: 120, rotateX: 18, rotateY: -22, "--mb": "5px", duration: 0.85, ease: "power2.in" });
      tl.to(card, { y: H * 0.24, rotateZ: 150, "--mb": "0px", duration: 0.28, ease: "power2.out" });

      // → Section 5 (CENTER wide): graze top edge, spin hard
      tl.to(card, { x: 0, y: H * 0.22, rotateZ: 520, rotateX: 0, rotateY: 0, scale: mobile ? 1 : 1.06, "--mb": "6px", duration: 0.95, ease: "power2.inOut" });
      tl.to(card, { y: H * 0.3, rotateZ: 545, "--mb": "0px", duration: 0.28, ease: "power2.out" });

      // → Section 6: fall + land flat on the table
      tl.to(card, { x: 0, y: H * 0.44, rotateZ: 560, scale: 1, "--mb": "6px", duration: 0.7, ease: "power2.in" });
      tl.to(card, { y: H * 0.6, rotateX: 76, rotateZ: 548, scale: mobile ? 1.05 : 1.14, "--mb": "0px", duration: 0.55, ease: "power4.out" });
    };

    build();

    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (tl && tl.scrollTrigger) tl.scrollTrigger.kill();
        if (tl) tl.kill();
        build();
        ScrollTrigger.refresh();
      }, 250);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(t);
      if (tl && tl.scrollTrigger) tl.scrollTrigger.kill();
      if (tl) tl.kill();
    };
  }, []);

  return (
    <div className="card-stage" data-testid="falling-card-stage" aria-hidden>
      <div
        ref={cardRef}
        className="card-3d"
        style={{ filter: "blur(var(--mb, 0px))" }}
      >
        <div className="card-idle">
          <div className="card-glow" />
          <KingCard />
        </div>
      </div>
    </div>
  );
}
